import { useMemo, ReactNode, useRef, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useQueryClient } from '@tanstack/react-query'
import { ThreadPackageProvider, ThreadPackageConfig } from '@timely/thread-package'
import * as MemberSelectors from '../../redux/selectors/members'
import Config from '../../config'
import { getFromStorage } from 'util/storage'
import * as SessionKeys from 'util/session-constants'

interface ThreadPackageProviderWrapperProps {
  children: ReactNode
}

/**
 * Wrapper component for ThreadPackageProvider that integrates with member-web Redux state
 * Only configures ThreadPackage on routes that need it (dashboard, messaging) and waits until
 * all required data is complete to prevent race conditions during SSO login
 */
export function ThreadPackageProviderWrapper({ children }: ThreadPackageProviderWrapperProps) {
  const queryClient = useQueryClient()
  const authToken = getFromStorage(SessionKeys.TOKEN)

  // Track current pathname without requiring router context
  const [currentPath, setCurrentPath] = useState(window.location.pathname)

  useEffect(() => {
    // Listen for route changes by checking pathname periodically
    // This is needed since we're outside the Router context
    const checkPath = () => {
      const newPath = window.location.pathname
      if (newPath !== currentPath) {
        setCurrentPath(newPath)
      }
    }

    const interval = setInterval(checkPath, 100) // Check every 100ms

    return () => clearInterval(interval)
  }, [currentPath])

  // Get member data from Redux
  const loggedInMemberId = useSelector(MemberSelectors.getLoggedInMemberId)
  const currentMember = useSelector(MemberSelectors.getMember(loggedInMemberId))

  // Use ref to maintain stable config reference and prevent unnecessary re-renders
  const stableConfigRef = useRef<ThreadPackageConfig | null>(null)

  // Check if we're NOT on an auth route (more stable, doesn't recalculate on every page change)
  const isValidRoute = useMemo(() => {
    // ThreadPackage is valid for all routes EXCEPT auth routes
    // This prevents config recalculation during SSO and is more stable
    return !currentPath.includes('/auth')
  }, [currentPath])

  // Check if we have complete data required for ThreadPackage to function properly
  // AND we're on a valid route that actually needs it
  const isDataComplete = useMemo(() => {
    return !!(
      isValidRoute &&
      authToken &&
      loggedInMemberId &&
      currentMember?.email &&
      currentMember?.firstName !== undefined &&
      currentMember?.lastName !== undefined
    )
  }, [isValidRoute, authToken, loggedInMemberId, currentMember])

  const config = useMemo((): ThreadPackageConfig => {
    // If data is not complete, return the exact same stable reference
    if (!isDataComplete) {
      // Return existing stable config if available, otherwise create and store initial placeholder
      if (!stableConfigRef.current) {
        stableConfigRef.current = {
          apiBaseUrl: Config.memberUrl,
          tenant: 'member',
          authToken: '',
          userId: '',
          user: {
            id: '',
            name: '',
            email: '',
            role: 'member',
            memberId: '',
          },
        }
      }
      return stableConfigRef.current
    }

    // Only create new config when we have complete data AND it's actually different
    const newConfig: ThreadPackageConfig = {
      apiBaseUrl: Config.memberUrl,
      tenant: 'member',
      authToken: authToken,
      userId: loggedInMemberId,
      user: {
        id: loggedInMemberId,
        name: currentMember ? `${currentMember.firstName || ''} ${currentMember.lastName || ''}`.trim() : '',
        email: currentMember?.email || '',
        role: 'member',
        memberId: loggedInMemberId,
      },
    }

    // Only update stable reference if the config has actually changed
    const currentStableConfig = stableConfigRef.current
    if (
      !currentStableConfig ||
      currentStableConfig.authToken !== newConfig.authToken ||
      currentStableConfig.userId !== newConfig.userId ||
      currentStableConfig.user.email !== newConfig.user.email ||
      currentStableConfig.user.name !== newConfig.user.name
    ) {
      stableConfigRef.current = newConfig
      return newConfig
    }

    // Return the same stable reference if nothing meaningful changed
    return currentStableConfig
  }, [
    isDataComplete, // Primary dependency - only recreate when completeness changes
    authToken,
    loggedInMemberId,
    currentMember, // Include the full currentMember object
  ])

  return (
    <ThreadPackageProvider config={config} queryClient={queryClient}>
      {children}
    </ThreadPackageProvider>
  )
}
